import { useState, useEffect, useRef, useCallback } from "react";

import { createPortal } from "react-dom"; // portal for calendar

import { motion, AnimatePresence } from "framer-motion";

import { FaChevronLeft } from "react-icons/fa";

import { PALETTE } from "../constants";

import {
  toLocalYYYYMMDD,
  parseYMD,
  startOfMonth,
  endOfMonth,
  addMonths,
  sameYMD,
} from "../dateUtils";

/* -------- Redbus-like Calendar Popover (portal, single instance) -------- */
const CalendarPopover = ({
  anchorRef,
  open,
  value,
  minDateString,
  onChange,
  onClose,
  palette = PALETTE,
}) => {
  const popRef = useRef(null);
  const today = new Date();
  const minDate = minDateString ? parseYMD(minDateString) : today;
  const selected = value ? parseYMD(value) : null;

  const [viewMonth, setViewMonth] = useState(
    selected ? startOfMonth(selected) : startOfMonth(today)
  );

  useEffect(() => {
    if (!open) return;

    const onDocClick = (e) => {
      if (
        popRef.current &&
        !popRef.current.contains(e.target) &&
        anchorRef.current &&
        !anchorRef.current.contains(e.target)
      ) {
        onClose?.();
      }
    };
    const onEsc = (e) => e.key === "Escape" && onClose?.();

    document.addEventListener("mousedown", onDocClick);
    document.addEventListener("keydown", onEsc);
    return () => {
      document.removeEventListener("mousedown", onDocClick);
      document.removeEventListener("keydown", onEsc);
    };
  }, [open, onClose, anchorRef]);

  useEffect(() => {
    if (selected) setViewMonth(startOfMonth(selected));
  }, [value]);

  const [pos, setPos] = useState({ top: 0, left: 0, width: 0 });

  const recalc = useCallback(() => {
    const el = anchorRef?.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    const width = 360;
    let left = r.left;
    const maxLeft = window.innerWidth - width - 12;
    if (left > maxLeft) left = Math.max(12, maxLeft);
    left = Math.max(12, left);
    const top = r.bottom + 10;
    setPos({ top, left, width });
  }, [anchorRef]);

  useEffect(() => {
    if (!open) return;
    recalc();
    const onResize = () => recalc();
    const onScroll = () => recalc();
    window.addEventListener("resize", onResize);
    window.addEventListener("scroll", onScroll, true);
    return () => {
      window.removeEventListener("resize", onResize);
      window.removeEventListener("scroll", onScroll, true);
    };
  }, [open, recalc]);

  if (!open) return null;

  const start = startOfMonth(viewMonth);
  const end = endOfMonth(viewMonth);
  const firstDow = (start.getDay() + 6) % 7;
  const totalCells = firstDow + end.getDate();
  const rows = Math.ceil(totalCells / 7);
  const cells = Array.from({ length: rows * 7 }, (_, i) => {
    const dayNum = i - firstDow + 1;
    if (dayNum < 1 || dayNum > end.getDate()) return null;
    return new Date(viewMonth.getFullYear(), viewMonth.getMonth(), dayNum);
  });

  const isDisabled = (d) =>
    d < new Date(new Date().toDateString()) || d < minDate;

  const selectDay = (d) => {
    if (isDisabled(d)) return;
    onChange?.(toLocalYYYYMMDD(d));
    onClose?.();
  };

  return createPortal(
    <div className="fixed inset-0 z-[9998]">
      <div
        className="absolute inset-0 bg-transparent"
        onMouseDown={(e) => {
          if (popRef.current && popRef.current.contains(e.target)) return;
          onClose?.();
        }}
      />

      <AnimatePresence>
        <motion.div
          ref={popRef}
          initial={{ opacity: 0, y: -8, scale: 0.98 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          exit={{ opacity: 0, y: -6, scale: 0.98 }}
          transition={{ duration: 0.18 }}
          className="fixed z-[9999] bg-white rounded-2xl shadow-[0_15px_40px_-10px_rgba(0,0,0,0.25)] border border-gray-100 overflow-hidden"
          style={{ top: pos.top, left: pos.left, width: pos.width }}
          onMouseDown={(e) => e.stopPropagation()}
        >
          <div
            className="px-4 pt-4 pb-2 border-b"
            style={{ borderColor: PALETTE.borderLight }}
          >
            <div
              className="text-xs uppercase font-medium tracking-wider"
              style={{ color: PALETTE.textLight }}
            >
              Date of Journey
            </div>

            <div className="flex items-center justify-between mt-1">
              <div
                className="text-base font-semibold"
                style={{ color: PALETTE.textDark }}
              >
                {value
                  ? parseYMD(value).toLocaleDateString("en-GB", {
                      day: "numeric",
                      month: "short",
                      year: "numeric",
                    })
                  : "Select Date"}
              </div>

              <div className="flex gap-2">
                <motion.button
                  whileHover={{ scale: 1.06 }}
                  whileTap={{ scale: 0.96 }}
                  type="button"
                  onClick={() => setViewMonth(addMonths(viewMonth, -1))}
                  className="group w-9 h-9 rounded-full border bg-gray-50 hover:bg-red-50 transition flex items-center justify-center active:scale-[0.98] shadow-[0_1px_2px_rgba(0,0,0,0.06)]"
                  style={{ borderColor: PALETTE.borderLight }}
                  aria-label="Previous month"
                >
                  <FaChevronLeft
                    className="text-[15px] transition-colors"
                    style={{ color: PALETTE.textDark }}
                  />
                </motion.button>

                <motion.button
                  whileHover={{ scale: 1.06 }}
                  whileTap={{ scale: 0.96 }}
                  type="button"
                  onClick={() => setViewMonth(addMonths(viewMonth, 1))}
                  className="group w-9 h-9 rounded-full border bg-gray-50 hover:bg-red-50 transition flex items-center justify-center active:scale-[0.98] shadow-[0_1px_2px_rgba(0,0,0,0.06)]"
                  style={{ borderColor: PALETTE.borderLight }}
                  aria-label="Next month"
                >
                  <FaChevronLeft
                    className="text-[15px] rotate-180 transition-colors"
                    style={{ color: PALETTE.textDark }}
                  />
                </motion.button>
              </div>
            </div>

            <div
              className="mt-1 text-sm font-medium"
              style={{ color: PALETTE.textDark }}
            >
              {viewMonth.toLocaleString("en-GB", {
                month: "long",
                year: "numeric",
              })}
            </div>
          </div>

          <div
            className="grid grid-cols-7 text-center text-xs py-2"
            style={{ color: PALETTE.textLight }}
          >
            {["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"].map((d) => (
              <div key={d} className="select-none">
                {d}
              </div>
            ))}
          </div>

          <div className="grid grid-cols-7 gap-y-1 px-2 pb-3">
            {cells.map((d, idx) => {
              if (!d) return <div key={idx} />;

              const selectedDay = value && sameYMD(parseYMD(value), d);
              const isToday = sameYMD(new Date(), d);
              const disabled = isDisabled(d);

              let classes =
                "mx-auto my-1 flex items-center justify-center w-9 h-9 rounded-full text-sm transition select-none ";
              if (disabled) classes += "text-gray-300 cursor-not-allowed";
              else if (selectedDay) classes += "text-white font-semibold";
              else classes += "hover:bg-red-50 cursor-pointer";

              return (
                <button
                  key={idx}
                  type="button"
                  className={classes}
                  onClick={() => selectDay(d)}
                  style={{
                    backgroundColor: selectedDay ? PALETTE.primaryRed : undefined,
                    border:
                      isToday && !selectedDay
                        ? `1px solid ${PALETTE.primaryRed}`
                        : undefined,
                    color: disabled
                      ? "#D1D5DB"
                      : selectedDay
                      ? "#FFFFFF"
                      : PALETTE.textDark,
                  }}
                  disabled={disabled}
                >
                  {d.getDate()}
                </button>
              );
            })}
          </div>

          <div
            className="px-4 py-3 border-t flex items-center justify-between"
            style={{ borderColor: PALETTE.borderLight }}
          >
            <div className="space-x-3">
              <button
                type="button"
                onClick={() => {
                  const t = new Date();
                  onChange?.(toLocalYYYYMMDD(t));
                  onClose?.();
                }}
                className="text-sm font-medium"
                style={{ color: PALETTE.accentBlue }}
              >
                Today
              </button>
              <button
                type="button"
                onClick={() => {
                  const tm = new Date();
                  tm.setDate(tm.getDate() + 1);
                  onChange?.(toLocalYYYYMMDD(tm));
                  onClose?.();
                }}
                className="text-sm font-medium"
                style={{ color: PALETTE.accentBlue }}
              >
                Tomorrow
              </button>
            </div>

            <button
              type="button"
              onClick={onClose}
              className="text-xs px-3 py-1.5 rounded-lg border hover:bg-gray-50"
              style={{
                borderColor: PALETTE.borderLight,
                color: PALETTE.textLight,
              }}
            >
              Close
            </button>
          </div>
        </motion.div>
      </AnimatePresence>
    </div>,
    document.body
  );
};

export default CalendarPopover;
